import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  getStoryContentsById,
  getTemplateContentsById,
  processStoryContentOrTemplateContent
} from '@opengov/stories-shared/graphQL';
import type { IStoryContent } from '@opengov/stories-shared/types';
import { orderStoryContents, sortStoryContents } from '@opengov/stories-shared/utils';

import { postQuery } from '../effects/publications/postQuery';
import { storyViewQueryKeys } from '../components/StoryView/hooks/storyViewQueryKeys';

type RefreshTarget = { storyId: string; storyContentId: string; entityId: number; isTemplate: boolean };
type PendingRefresh = RefreshTarget & { queryClient: QueryClient };

/** `postQuery` is untyped; this narrows its response to the one field read here. */
const hasStoryContents = (data: unknown): data is { storyContents: unknown[] } =>
  typeof data === 'object' && data !== null && 'storyContents' in data && Array.isArray(data.storyContents);

/** Loads a story's sections the same way `useStoryContents` does, without going through the hook. */
async function fetchStorySections(storyId: string, entityId: number, isTemplate: boolean): Promise<IStoryContent[]> {
  const variables: Record<string, string | number> = {
    [isTemplate ? 'storyTemplateId' : 'storyId']: storyId,
    limit: 100,
    ...(!isTemplate && { entityId })
  };
  const data: unknown = await postQuery(isTemplate ? getTemplateContentsById : getStoryContentsById, variables);
  const raw: IStoryContent[] = (hasStoryContents(data) ? data.storyContents : []).map(
    processStoryContentOrTemplateContent
  );
  return orderStoryContents(sortStoryContents(raw));
}

/** Every section launched since the drawer last closed, keyed by section id, so launching a second tile never drops the first. */
const pending = new Map<string, PendingRefresh>();

/** Returns the function to call when OG Assist is launched for a section, so that section is re-read when the drawer closes. */
export function useMarkSummaryRefreshPending(): (target: RefreshTarget) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (target: RefreshTarget) => {
      pending.set(target.storyContentId, { ...target, queryClient });
    },
    [queryClient]
  );
}

/** Re-reads one story's sections once and re-seeds each launched section whose saved content changed. */
async function refreshStorySections(targets: PendingRefresh[]): Promise<void> {
  const [{ queryClient, storyId, entityId, isTemplate }] = targets;
  const key = storyViewQueryKeys.storyContents({ storyId, entityId, isTemplate });
  if (!queryClient.getQueryData<IStoryContent[]>(key)) return;

  try {
    const fresh = await fetchStorySections(storyId, entityId, isTemplate);
    const launchedIds = new Set(targets.map((target) => target.storyContentId));
    const changed = new Map(
      fresh.filter((section) => launchedIds.has(section._id)).map((section) => [section._id, section])
    );
    if (changed.size === 0) return;

    queryClient.setQueryData<IStoryContent[]>(key, (old = []) =>
      old.map((storyContent) => {
        const updated = changed.get(storyContent._id);
        return updated && storyContent.content !== updated.content ? { ...updated, shouldUpdate: true } : storyContent;
      })
    );
  } catch (error) {
    console.error('Could not refresh the story section after OG Assist closed', error);
  }
}

/**
 * OG Assist writes the summary into the saved story on the server, so the open editor's copy of each launched
 * section is stale. Re-reads every launched-but-unrefreshed section (one fetch per story) and, for those that
 * changed, re-seeds the editor — the same `shouldUpdate` mechanism the embed refresh uses.
 */
export async function refreshPendingSummarySection(): Promise<void> {
  const targets = [...pending.values()];
  pending.clear();

  const byStory = new Map<string, PendingRefresh[]>();
  targets.forEach((target) => {
    const groupKey = `${target.isTemplate ? 'template' : 'story'}:${target.storyId}:${target.entityId}`;
    byStory.set(groupKey, [...(byStory.get(groupKey) ?? []), target]);
  });

  await Promise.all([...byStory.values()].map(refreshStorySections));
}

/** Passed to the NavBar as OG Assist's `onDrawerToggle`: when the drawer closes, re-read every launched section. */
export function handleOgAssistDrawerToggle(open: boolean): void {
  if (!open) void refreshPendingSummarySection();
}
